import React from 'react';
import { GlobalLogic } from '../../shared/logic/global-logic';
import { ILandoltContainer, IDirection, TestLogic } from '../logic/test-logic';
import { DebugBox } from '../../shared/components/debug-box/debug-box';
import { StringMap, VidebisTest } from '../../shared/util/types';
import { EyeTestInputController } from './eye-test-input.controller';
import { TestLogicFactory } from '../logic/test-logic-factory';
import { TextTestController } from './text-test.controller';
import { MichelsonTestLogic } from '../logic/michelson-test-logic';
import { ContrastTestLogic } from '../logic/contrast-test-logic';
import { getQuery } from '../../shared/util/functions';
import '../styles/eye-test.scss';
import { postTelemetry } from '../../result/util/axios';
import { getContrastResultTelemetryData, getMichelsonResultTelemetryData, getVisusResultTelemetryData } from '../../result/util/telemetry';


interface Props {
	history: any;
	location: any;
}

interface State {
	updateFlag: boolean;
	step: number;
}

export class EyeTestController extends React.PureComponent<Props, State> {
	state: State = {
		updateFlag: false,
		step: 1
	};

	private _globalLogic: GlobalLogic = GlobalLogic.getInstance();
	private readonly _testLogic: TestLogic | null;
	private readonly _testType: VidebisTest;

	constructor(props: Props) {
		super(props);
		const qObj = getQuery(this.props.location.search);
		if (
			!qObj ||
			!qObj.type ||
			!['eye-test', 'contrast-test', 'michelson-test', 'text-test'].includes(qObj.type as string)) {
			this.props.history.push('/setup?step=4');
		}
		if (qObj.type === 'eye-test') {
			this._testType = 'eye-test';
			this._testLogic = TestLogicFactory.getInstanceOf('eye-test-logic');
		} else if (qObj.type === 'contrast-test') {
			this._testType = 'contrast-test';
			this._testLogic = TestLogicFactory.getInstanceOf('contrast-test-logic');
		} else if (qObj.type === 'michelson-test') {
			this._testType = 'michelson-test';
			this._testLogic = TestLogicFactory.getInstanceOf('michelson-test-logic');
		} else {
			this._testType = 'text-test';
			this._testLogic = null;
		}
	}

	componentDidMount(): void {
		if (this._testLogic) {
			this._testLogic.resetState();
			this.setState({ updateFlag: !this.state.updateFlag });
		}
	}

	render(): any {
		if (this._testType === 'text-test') {
			return <TextTestController history={this.props.history} location={this.props.location} />;
		} else if (this._testLogic) {
			const current: ILandoltContainer = this._testLogic.current();
			return (
				<>
					<section className={'eye-test--main'}>
						<EyeTestInputController
							landoltDirection={current.direction.code}
							landoltBaseSize={current.baseSize}
							landoltColor={current.color}
							onDirectionInput={this.handleDirectionInput}
						/>
						<DebugBox content={this.getDebugContent()} />
					</section>
				</>
			);
		} else {
			return null;
		}
	}

	private handleDirectionInput = (direction: IDirection): void => {
		if (this._testLogic && this._testLogic.apply(direction)) {
			// Test is not finished yet
			this.setState({ updateFlag: !this.state.updateFlag });
		} else {
			// Test has been finished. Mark it and get to the results page
			this._globalLogic.markTestAsFinished(this._testType);
			const query = getQuery(this.props.location.search);
			if (query['all-tests']) {
				let nextTest: string | null = null;
				if (this._testType === 'eye-test') {
					postTelemetry('visus', getVisusResultTelemetryData());
					nextTest = 'contrast-test';
				} else if (this._testType === 'contrast-test') {
					postTelemetry('contrast', getContrastResultTelemetryData());
					nextTest = 'michelson-test';
				} else if (this._testType === 'michelson-test') {
					postTelemetry('michelson', getMichelsonResultTelemetryData());
					nextTest = 'text-test';
				}
				this.props.history.push(`/test-explanation?test=${nextTest}&all-tests=true`);
			} else {
				this.props.history.push(
					'/result?from=' + this._testType + (query['all-tests'] ? '&all-tests=true' : '')
				);
			}
		}
	};

	private getDebugContent = (): StringMap<string> => {
		const current = this._testLogic!.current(); // We check for nullness in the render-fn
		const debugContent: StringMap<string> = {
			'Id': current.id.toString(),
			'Size': current.baseSize.toString(),
			'Ideal distance': this._globalLogic.distance.toFixed(3).toString(),
			'Visus on 1m': current.visusOneMeter.toString(),
			'Visus on ideal distance':
				(current.visusOneMeter * (this._globalLogic.distance / 100)).toFixed(3).toString(),
			'Correct guesses': current.correctGuess.toString(),
			'Wrong guesses': current.wrongGuess.toString(),
			'Scale factor': this._globalLogic.scaleFactor.toFixed(3).toString()
		};

		if (this._testLogic) {
			debugContent['Current color'] = this._testLogic.current().color;
			if (this._testLogic.type() === 'ContrastTestLogic') {
				debugContent['Current percentage'] =
					(this._testLogic as ContrastTestLogic).getCurrentOpacityPercentage().toString();
				debugContent['Correct guesses'] = (this._testLogic as ContrastTestLogic).getCurrentCorrectGuesses().toString();
				debugContent['Wrong guesses'] = (this._testLogic as ContrastTestLogic).getCurrentWrongGuesses().toString();
			}
			debugContent['Test Logic type'] = this._testLogic.type();
		}

		if (this._testType === 'michelson-test') {
			const currentMode = (this._testLogic as MichelsonTestLogic).getMode();
			const result = (this._testLogic as MichelsonTestLogic).getResult();
			debugContent['Current mode'] = currentMode;
			debugContent['Last seen (reduced opacity)'] = result.lastReducedOpacity.id.toString();
			debugContent['Last seen (current mode)'] = result.lastFullOpacity.id.toString();
		}

		return debugContent;
	};
}
